/**
 * Worker message protocol for the off-main-thread NS solver.
 *
 * PHASE 1 — TYPES ONLY. Nothing here is wired into a runtime path; these are
 * the contract the main thread and the solver worker will agree on in a later
 * phase.
 *
 * ──────────────────────────────────────────────────────────────────────────
 * WHY EVERY MESSAGE CARRIES A MONOTONIC SEQUENCE NUMBER
 * ──────────────────────────────────────────────────────────────────────────
 * The migration plan flags MESSAGE REORDERING and STALE STATE as CRITICAL
 * risks. `postMessage` delivery order within a single channel is ordered, but
 * the LOGICAL pipeline is not safe by construction:
 *   • The UI can fire a fresh StartRequest (network rebuild / settings change)
 *     while a StepRequest for the PREVIOUS network is still in flight. A late
 *     ResultResponse from the old network must be IGNORED, not applied.
 *   • A CancelRequest must be able to invalidate every in-flight step.
 *   • ProgressResponses are advisory and may arrive after the final Result.
 *
 * Therefore EVERY message — request and response — carries a `seq`:
 *   • Requests: the main thread assigns a strictly increasing `seq` from a
 *     single counter (see `createSequencer`). The worker MUST ignore any
 *     request whose `seq` is ≤ the highest `seq` it has already begun
 *     processing (a stale/reordered request).
 *   • Responses: the worker echoes the `requestSeq` of the request that caused
 *     it, plus its own monotonic `seq`. The main thread MUST drop any response
 *     whose `requestSeq` is older than the latest request it has dispatched
 *     (the result belongs to a superseded network/step).
 *
 * `epoch` partitions sequence space by network identity: a StartRequest opens
 * a new epoch; every StepRequest/Result for that network shares the epoch.
 * A response from a prior epoch is unconditionally stale and dropped. This is
 * the second guard against applying a step computed against an old network.
 */

import type { SerializedSolverState } from './serialization';

/** Discriminator for every message crossing the worker boundary. */
export type NSWorkerMessageType =
  | 'start'
  | 'step'
  | 'cancel'
  | 'progress'
  | 'result'
  | 'error';

/** Direction tag — purely documentary; the `type` already disambiguates. */
export type MessageDirection = 'request' | 'response';

/**
 * Fields shared by EVERY message. `seq` is strictly increasing per sender;
 * `epoch` is the network-identity generation (bumped on each StartRequest).
 */
export interface NSWorkerEnvelope {
  /** Strictly-increasing per-sender sequence number. Never reused. */
  seq: number;
  /** Network generation. A StartRequest opens a new epoch. */
  epoch: number;
  type: NSWorkerMessageType;
  direction: MessageDirection;
}

// ──────────────────────────────────────────────────────────────────────────
// Requests (main thread → worker)
// ──────────────────────────────────────────────────────────────────────────

/**
 * Initialize/replace the worker's network with a fresh serialized state and
 * open a new epoch. Any in-flight step from a prior epoch is invalidated.
 */
export interface StartRequest extends NSWorkerEnvelope {
  type: 'start';
  direction: 'request';
  /** Full serialized solver state to seed the worker with. */
  state: SerializedSolverState;
  /**
   * Wall-clock dt budget the UI wants advanced per StepRequest, in seconds.
   * The worker still applies its own CFL sub-stepping (mirrors stepAdapter).
   */
  dt: number;
  /** Optional user knobs threaded into the SIMPLE config (mirrors stepAdapter). */
  settings?: {
    relaxationFactor?: number;
    convergenceTolerance?: number;
    autoTimeStep?: boolean;
  };
}

/**
 * Advance the in-worker network by one UI step (which may CFL-sub-step
 * internally). Carries the epoch it belongs to so a stale step is droppable.
 */
export interface StepRequest extends NSWorkerEnvelope {
  type: 'step';
  direction: 'request';
  /** dt budget for this step, in seconds. */
  dt: number;
  /**
   * Optional full state to run against. When omitted, the worker advances its
   * OWN retained state (the steady-state design). A provided state lets the
   * caller force an authoritative resync and defeats any stale-state drift.
   */
  state?: SerializedSolverState;
  /**
   * User knobs threaded into the SIMPLE config for THIS step (mirrors
   * stepAdapter's deriveSolverConfig + autoTimeStep). Carried per-step because
   * the client posts StepRequests without a prior StartRequest; without this
   * the worker would run with base relaxation/tolerance/fixed-dt and DIVERGE
   * from the main-thread runNSStep whenever the user changed a knob.
   */
  settings?: {
    relaxationFactor?: number;
    convergenceTolerance?: number;
    autoTimeStep?: boolean;
  };
}

/** Cancel all in-flight work for the given epoch (or all epochs if omitted). */
export interface CancelRequest extends NSWorkerEnvelope {
  type: 'cancel';
  direction: 'request';
  /** When set, cancel only work for this epoch; otherwise cancel everything. */
  cancelEpoch?: number;
}

export type NSWorkerRequest = StartRequest | StepRequest | CancelRequest;

// ──────────────────────────────────────────────────────────────────────────
// Responses (worker → main thread)
// ──────────────────────────────────────────────────────────────────────────

/**
 * Advisory mid-solve progress (e.g. per CFL sub-step). May arrive after the
 * final ResultResponse for the same request and must be treated as best-effort.
 */
export interface ProgressResponse extends NSWorkerEnvelope {
  type: 'progress';
  direction: 'response';
  /** `seq` of the request that triggered this work. */
  requestSeq: number;
  /** Sub-steps completed so far / total planned for this step. */
  substep: number;
  substepsTotal: number;
  /** Live mass residual snapshot (advisory). */
  massResidual: number;
}

/**
 * Final result of a Start or Step: the new serialized state plus the same
 * diagnostics `runNSStep` surfaces today. The main thread applies it ONLY if
 * `requestSeq`/`epoch` are still current.
 */
export interface ResultResponse extends NSWorkerEnvelope {
  type: 'result';
  direction: 'response';
  /** `seq` of the request that produced this result. */
  requestSeq: number;
  /** New serialized solver state after the step. */
  state: SerializedSolverState;
  /** dt actually advanced this step, in seconds. */
  dt: number;
  converged: boolean;
  residuals: {
    mass: number;
    momentum: number;
    energy: number;
  };
  /** First-law closure percentage (null when not yet computable). */
  energyClosurePct: number | null;
  warnings: string[];
}

/** Worker-side failure for a given request (parse/solve/unknown-type). */
export interface ErrorResponse extends NSWorkerEnvelope {
  type: 'error';
  direction: 'response';
  requestSeq: number;
  message: string;
}

export type NSWorkerResponse = ProgressResponse | ResultResponse | ErrorResponse;

export type NSWorkerMessage = NSWorkerRequest | NSWorkerResponse;

// ──────────────────────────────────────────────────────────────────────────
// Sequence/epoch helpers (the staleness guards live here)
// ──────────────────────────────────────────────────────────────────────────

/** A monotonic counter. `next()` never returns the same value twice. */
export interface Sequencer {
  next(): number;
  /** The most recently issued value (the seed value before any `next()`). */
  current(): number;
}

/**
 * Create a strictly-increasing sequence generator. The first `next()` returns
 * `start + 1`. Used by the main thread for request `seq` and by the worker for
 * response `seq`.
 */
export function createSequencer(start = 0): Sequencer {
  let n = start;
  return {
    next: () => ++n,
    current: () => n,
  };
}

/**
 * Decide whether an incoming RESPONSE is stale and must be dropped.
 *
 * A response is fresh iff it belongs to the current epoch AND its `requestSeq`
 * is not older than the newest request the main thread has dispatched. (We
 * accept `requestSeq === latestRequestSeq`; a strictly-older one is stale.)
 */
export function isResponseStale(
  resp: Pick<NSWorkerResponse, 'epoch' | 'requestSeq'>,
  currentEpoch: number,
  latestRequestSeq: number,
): boolean {
  if (resp.epoch !== currentEpoch) return true;
  return resp.requestSeq < latestRequestSeq;
}

/**
 * Decide whether an incoming REQUEST is stale at the worker.
 *
 * A request is stale iff it belongs to an epoch the worker has already
 * superseded, OR its `seq` is not newer than the highest request `seq` the
 * worker has already started processing (a reordered/duplicate request).
 */
export function isRequestStale(
  req: Pick<NSWorkerRequest, 'epoch' | 'seq'>,
  workerEpoch: number,
  highestProcessedSeq: number,
): boolean {
  if (req.epoch < workerEpoch) return true;
  return req.seq <= highestProcessedSeq;
}
